import { useMemo } from 'react';
import {
  Observable,
  ApolloClient,
  ApolloLink,
  InMemoryCache,
  split,
  HttpLink,
  gql,
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { useAuthStore, useUserStore } from '../stores';

/* ============================================
   GRAPHQL MUTATION DEFINITIONS
   ============================================ */

const REFRESH_ACCESS_TOKEN_RIDER = gql`
  mutation RefreshAccessTokenRider {
    refreshAccessTokenRider {
      accessToken
      rider {
        id
        name
      }
    }
  }
`;

const REFRESH_ACCESS_TOKEN_DRIVER = gql`
  mutation RefreshAccessTokenDriver {
    refreshAccessTokenDriver {
      accessToken
      driver {
        id
        name
      }
    }
  }
`;

const LOGOUT_RIDER = gql`
  mutation LogoutRider {
    logoutRider {
      message
    }
  }
`;

const LOGOUT_DRIVER = gql`
  mutation LogoutDriver {
    logoutDriver {
      message
    }
  }
`;

/* ============================================
   HELPER FUNCTIONS
   ============================================ */

// Check if the token is expired. `leewaySeconds` treats a token as expired
// slightly early so we never present one that dies in transit. Exported so
// PersistLogin can tell whether a persisted token needs refreshing.
export const isTokenExpired = (accessToken, leewaySeconds = 30) => {
  if (!accessToken) return true;
  
  try {
    // JWT payloads are base64url — convert to standard base64 before atob,
    // otherwise a payload containing '-' or '_' throws and the token is
    // wrongly treated as expired.
    const base64 = accessToken
      .split('.')[1]
      .replace(/-/g, '+')
      .replace(/_/g, '/');
    const decoded = JSON.parse(atob(base64));
    return decoded.exp < Date.now() / 1000 + leewaySeconds;
  } catch (error) {
    return true;
  }
};

// Helper to get current auth state (for use in callbacks)
const getAuthState = () => useAuthStore.getState();
const getUserState = () => useUserStore.getState();

/* ============================================
   WEBSOCKET CLIENT (module scope — created once)
   ============================================ */

// graphql-ws only evaluates connectionParams when a connection is
// (re)established. Without a restart, a socket opened while logged out
// (e.g. browsing Available Drivers) stays ANONYMOUS after login — and the
// server-side subscription filters would then deliver no ride events.
// NOTE: these options live at the TOP level of createClient — nesting them
// under an `options` key silently discards them.
const wsClient = createClient({
  url: import.meta.env.VITE_API_GRAPHQL_WS,
  // Retry strategy with exponential backoff.
  retryAttempts: 5,
  retryWait: async (retries) => {
    await new Promise((resolve) =>
      setTimeout(resolve, Math.min(1000 * Math.pow(2, retries), 10000))
    );
  },
  connectionParams: () => {
    // Never present an expired token — connect anonymously instead and let
    // the restart-on-token-change below re-establish an authenticated
    // socket once a refresh lands (same rule as mobile and admin).
    const { accessToken } = getAuthState();
    if (accessToken && !isTokenExpired(accessToken)) {
      return { authorization: `Bearer ${accessToken}` };
    }
    return {};
  },
});

// Restart the socket whenever the access token changes (login, logout,
// refresh) so active subscriptions re-establish with the new identity.
let lastWsToken = useAuthStore.getState().accessToken ?? null;
useAuthStore.subscribe((state) => {
  const token = state.accessToken ?? null;
  if (token === lastWsToken) return;
  lastWsToken = token;
  try {
    wsClient.restart();
  } catch (e) {
    // Socket not connected yet — nothing to restart.
  }
});

/* ============================================
   APOLLO CLIENT SETUP
   ============================================ */

// Shared in-flight refresh promise: when several requests hit 401 at the
// same time, they all await the SAME refresh instead of the extras getting
// `undefined` back and failing their retry.
let refreshPromise = null;

const useApollo = () => {
  // Get setters from stores (these are stable references)
  const { setAccessToken, resetAuthState } = useAuthStore();
  const { resetUserState } = useUserStore();

  // Reset Global State
  const resetGlobalState = () => {
    resetAuthState();
    resetUserState();
  };

  // Create the HTTP link for graphql queries and mutations
  const httpLink = new HttpLink({
    uri: import.meta.env.VITE_API_GRAPHQL_URI,
    credentials: 'include',
  });

  // Create the WS link for graphql subscriptions (wraps the module-scope
  // wsClient above).
  const wsLink = new GraphQLWsLink(wsClient);

  const authLink = new ApolloLink((operation, forward) => {
    // Only truly public operations that don't require authentication
    const publicOperations = [
      'LoginDriver',
      'LoginRider',
      'SignupRider',
      'RefreshAccessTokenRider',
      'RefreshAccessTokenDriver',
      'LogoutRider',
      'LogoutDriver',
    ];
    const operationName = operation.operationName;

    // Only add auth header for non-public operations
    if (!publicOperations.includes(operationName)) {
      // Get fresh token at request time
      const { accessToken } = getAuthState();

      // Only set auth header if token is a valid string
      if (typeof accessToken === 'string' && accessToken.length > 0) {
        operation.setContext({
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
      }
    }

    return forward(operation);
  });

  // Refresh the access token. Concurrent callers share one in-flight
  // refresh and all receive its result.
  const refreshAccessToken = (apolloClient) => {
    if (refreshPromise) return refreshPromise;

    refreshPromise = (async () => {
      try {
        // Refresh access token mutation.
        const { userType } = getUserState();
        const mutation =
          userType === 'rider'
            ? REFRESH_ACCESS_TOKEN_RIDER
            : REFRESH_ACCESS_TOKEN_DRIVER;

        const { data } = await apolloClient.mutate({ mutation });

        return data.refreshAccessTokenRider || data.refreshAccessTokenDriver;
      } catch (error) {
        // Error codes the backend actually throws for a dead session —
        // anything here means the refresh token is gone/rotated/expired and
        // the only way forward is a fresh login.
        const serverErrors = [
          'NO_REFRESH_TOKEN',
          'SESSION_INVALIDATED',
          'INVALID_TOKEN',
          'REFRESH_ACCESS_TOKEN_ERROR',
        ];

        const errorCode = error.graphQLErrors?.[0]?.extensions?.code;
        if (errorCode && serverErrors.includes(errorCode)) {
          forceLogout(apolloClient);
        }

        throw error;
      } finally {
        refreshPromise = null; // Allow the next refresh attempt
      }
    })();

    return refreshPromise;
  };

  // Force logout on auth failure
  const forceLogout = async (apolloClient) => {
    const { userType } = getUserState();
    const mutation = userType === 'rider' ? LOGOUT_RIDER : LOGOUT_DRIVER;

    // Try to notify server, but don't block on failure
    try {
      await apolloClient.mutate({ mutation });
    } catch (error) {
      // Continue with local cleanup even if server logout fails
    }

    // Always clean up local state
    resetGlobalState();
    await apolloClient.clearStore();
  };

  const errorLink = onError(({ graphQLErrors, operation, forward }) => {
    // Only fire on error.
    if (!graphQLErrors) return;

    // TODO: Delete console logs.
    // console.log('** Apollo Error **');

    // Operations that are public even for logged-in users, so a 401 on them
    // should NOT trigger a token refresh. CreateRide and GetRide do NOT
    // belong here: the backend protects both for logged-in riders, so an
    // expired token must refresh + retry or the core ride flow hard-fails.
    const publicOperations = [
      'GetAvailableDrivers',
      'GetUnavailableDrivers',
    ];

    for (const err of graphQLErrors) {
      const operationName = operation.operationName;
      const { userType } = getUserState();

      if (
        err.extensions?.code === 'UNAUTHORIZED' &&
        userType &&
        !publicOperations.includes(operationName)
      ) {
        const { accessToken } = getAuthState();

        if (isTokenExpired(accessToken)) {
          return new Observable((observer) => {
            refreshAccessToken(client)
              .then((newAccessTokenData) => {
                const newAccessToken = newAccessTokenData.accessToken;
                setAccessToken(newAccessToken);

                operation.setContext(({ headers = {} }) => ({
                  headers: {
                    ...headers,
                    Authorization: `Bearer ${newAccessToken}`,
                  },
                }));

                forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                });
              })
              .catch((error) => observer.error(error));
          });
        }
      }
    }
  });

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    ApolloLink.from([errorLink, authLink, httpLink])
  );

  const client = useMemo(() => {
    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache({
        // Add type policies to handle specific entity types
        typePolicies: {
          Query: {
            fields: {
              // For queries that should always be fresh
              getAvailableDrivers: {
                // After 30 seconds, consider the data stale
                maxAge: 30, // in seconds
              },
              getFavoriteDrivers: {
                maxAge: 30,
              },
              // TODO: Add other queries as needed
            },
          },
        },
      }),
      // Update default fetch policies
      defaultOptions: {
        watchQuery: {
          // Fetch from cache first, then network (for real-time updates)
          fetchPolicy: 'cache-and-network',
          // After first fetch, use cache but will be invalidated based on maxAge
          nextFetchPolicy: 'cache-first',
          // Errors won't be cached
          errorPolicy: 'none',
        },
        query: {
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all',
        },
        mutate: {
          // Don't require all queries to be refetched after mutations
          refetchQueries: false,
          // But make sure the cache is properly updated
          awaitRefetchQueries: false,
        },
      },
    });
  }, []);

  return client;
};

export default useApollo;
